import React, { useEffect, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { GlobalModal } from "../../../../global/GlobalModal";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { getVisitorById } from "../../api/visitorsApi";
import { resolveVisitorStatus } from "../../utils/visitorStatus";
import { formatPersonWithDepartment, safeErrorText } from "../../utils/visitorHelpers";
import RegisteredByCell from "../directory/RegisteredByCell";

function fmtDate(v) {
  if (!v) return "";
  try {
    const d = parseISO(String(v).slice(0, 10));
    return isValid(d) ? format(d, "dd MMM yyyy") : String(v).slice(0, 10);
  } catch {
    return String(v).slice(0, 10);
  }
}

function fmtTime(v) {
  if (!v) return "";
  const s = String(v);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function yesNo(value) {
  if (value === 1 || value === true) return "Yes";
  if (value === 0 || value === false) return "No";
  return "—";
}

function InfoRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "0.5rem 0",
        borderBottom: "1px solid #f1f5f9",
        gap: "1rem",
      }}
    >
      <span style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem" }}>{label}</span>
      <span
        style={{
          fontWeight: 500,
          color: "#1e293b",
          fontSize: "0.875rem",
          textAlign: "right",
          maxWidth: "60%",
        }}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3
        style={{
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--accent, #0ea5e9)",
          marginBottom: "0.5rem",
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

/** View visitor — same pattern as call-logs `ViewCallLogModal`. */
export default function ViewVisitorModal({ visitorId, onClose, isAdmin = false }) {
  const [visitor, setVisitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!visitorId) {
      setVisitor(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getVisitorById(visitorId);
        if (!cancelled) setVisitor(res?.data || null);
      } catch (err) {
        if (!cancelled) {
          setError(safeErrorText(err, "Failed to load visitor details"));
          setVisitor(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visitorId]);

  const statusLabel = visitor ? resolveVisitorStatus(visitor) : "";

  return (
    <GlobalModal
      open={Boolean(visitorId)}
      onClose={onClose}
      title="Visitor Details"
      description={
        visitor?.visitor_name
          ? `Viewing record for ${visitor.visitor_name}`
          : "View visitor information"
      }
      size="md"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          <GlobalButton variant="ghost" onClick={onClose}>
            Close
          </GlobalButton>
        </div>
      }
    >
      {loading ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "#64748b" }}>
          Loading visitor details…
        </div>
      ) : error ? (
        <div style={{ padding: "1rem", color: "var(--danger, #dc2626)" }} role="alert">
          {error}
        </div>
      ) : visitor ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <Section title="Visitor information">
            <InfoRow label="Visitor ID" value={visitor.visitor_code || visitor.id} />
            <InfoRow label="Visitor Name" value={visitor.visitor_name} />
            <InfoRow label="Mobile" value={visitor.mobile_number} />
            <InfoRow label="Address" value={visitor.address} />
            <InfoRow label="Purpose" value={visitor.purpose_name} />
            <InfoRow
              label="Person To Meet"
              value={formatPersonWithDepartment(visitor.person_to_meet_name, {
                department_name: visitor.person_to_meet_department,
                role_slug: visitor.person_to_meet_role_slug,
              })}
            />
            <InfoRow label="Entry Date" value={fmtDate(visitor.entry_date)} />
            <InfoRow label="In Time" value={fmtTime(visitor.in_time)} />
            <InfoRow label="Out Time" value={fmtTime(visitor.out_time)} />
          </Section>

          <Section title="Status & pass">
            <InfoRow label="Status" value={statusLabel} />
            <InfoRow label="Inside Premises" value={yesNo(visitor.is_inside)} />
            <InfoRow label="Visitor Pass Issued" value={yesNo(visitor.visitor_pass_issued)} />
          </Section>

          <Section title="ID & approval">
            <InfoRow label="ID Proof" value={visitor.id_proof_type_name} />
            <InfoRow label="ID Number" value={visitor.id_proof_number} />
            <InfoRow label="Approved By" value={visitor.approved_by_name} />
            {isAdmin ? (
              <InfoRow
                label="Registered By"
                value={<RegisteredByCell row={visitor} />}
              />
            ) : null}
            <InfoRow label="Remarks" value={visitor.remarks} />
          </Section>
        </div>
      ) : (
        <div style={{ padding: "1rem", color: "#64748b" }}>Visitor not found.</div>
      )}
    </GlobalModal>
  );
}
